// themeStore.js — global theme selector for Proxera Consistency.
//
// Four themes ship:
//   - 'cosmic'              — default. Electric blue accents, lava-lamp
//                             blobs, gradient typography, halo glows.
//   - 'professional-dark'   — office-grade dark. Saturated editorial
//                             blue, flat surfaces, no glow/blobs, solid
//                             (non-gradient) accent type.
//   - 'professional-light'  — office-grade light. Same palette intent
//                             but on a paper-white canvas.
//   - 'girlboss'            — pink / red accents on Cosmic's dark canvas,
//                             hearts instead of checks for completions.
//
// The theme is applied via `data-theme` on <html>. Every palette override
// lives in index.css under `[data-theme="<id>"]` so React never has to
// branch on theme — components stay theme-agnostic and CSS does the work.
// The one exception is the check→heart swap in Girlboss, which is handled
// by <CheckIcon> subscribing to this store.
//
// Persistence: localStorage key `proxera.theme.v2`. Applied synchronously
// before React mounts (main.jsx) so the first paint is already themed —
// no flash of the default look while the store initializes.
//
// A small migration reads the legacy v1 value ('professional' without the
// -dark suffix) and maps it forward so existing users don't get snapped
// back to Cosmic on upgrade.

const STORAGE_KEY_V2 = 'proxera.theme.v2';
const STORAGE_KEY_V1 = 'proxera.theme.v1';
const VALID = new Set(['cosmic', 'professional-dark', 'professional-light', 'girlboss']);

function readInitial() {
  try {
    const v2 = localStorage.getItem(STORAGE_KEY_V2);
    if (VALID.has(v2)) return v2;
    // Migrate legacy single-name 'professional' → 'professional-dark'
    const v1 = localStorage.getItem(STORAGE_KEY_V1);
    if (v1 === 'professional') {
      try { localStorage.setItem(STORAGE_KEY_V2, 'professional-dark'); } catch {}
      return 'professional-dark';
    }
    if (v1 === 'cosmic') return 'cosmic';
    return 'cosmic';
  } catch {
    return 'cosmic';
  }
}

let currentTheme = readInitial();
const listeners = new Set();

function apply(theme) {
  try {
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.setAttribute('data-theme', theme);
    }
  } catch {}
}

// Called once from main.jsx during boot. Safe to call again later.
export function applyToDocument() {
  apply(currentTheme);
}

export function getTheme() {
  return currentTheme;
}

export function setTheme(theme) {
  if (!VALID.has(theme)) return;
  if (theme === currentTheme) return;
  currentTheme = theme;
  try { localStorage.setItem(STORAGE_KEY_V2, theme); } catch {}
  apply(theme);
  for (const fn of listeners) {
    try { fn(theme); } catch {}
  }
}

// Pub/sub for React (useSyncExternalStore-compatible).
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Snapshot helper — useSyncExternalStore requires a referentially stable
// primitive, and a bare string fits that contract perfectly.
export function getSnapshot() {
  return currentTheme;
}

// ─── React hooks ───────────────────────────────────────────────────────
// Kept here (not a separate hooks file) so every theme consumer imports
// from the same module — easier to spot at review time who's subscribing.
import { useSyncExternalStore } from 'react';
import { resolveAccentColor } from './constants.js';

// Returns the active theme id, re-rendering the caller when it changes.
export function useTheme() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// Returns the resolved color for a habit's accent key under the current
// theme. Safe to use in render — re-renders when the theme store emits.
//   useAccentColor('blue')     → '#FF3E6C' under Girlboss, '#2D9CFF' under Cosmic
//   useAccentColor('amber')    → '#FFB547' under every theme (no override)
export function useAccentColor(accentKey) {
  const theme = useTheme();
  return resolveAccentColor(accentKey, theme);
}
